import { z } from 'zod';
import { createModel, heroPlatformSchema, QueryMany, QueryOne } from '../../common';
import type { HeroPlatform } from '../../common';
import { dateSchema, objectIdSchema } from '../../validation';

/**
 * Reason a refresh session was revoked. Used for audit / anomaly
 * investigation, not for gating client behavior.
 *
 * - `logout` — user explicitly signed out via `/auth/sign-out`.
 * - `rotation` — the previous refresh token was consumed by a
 *   successful `/auth/refresh` call (normal lifecycle).
 * - `reuse_detected` — a refresh token whose `jti` was already
 *   rotated out of the chain was replayed. Entire session is
 *   revoked as a compromise indicator.
 * - `admin_revoke` — a super-admin forced revocation (e.g. via
 *   `/auth/logout-all` on account block).
 * - `password_change` — reserved. Hero is OTP-only today, but the
 *   field exists so future re-auth events have a stable enum
 *   value.
 */
export const refreshSessionRevokeReasonSchema = z.enum([
  'logout',
  'rotation',
  'reuse_detected',
  'admin_revoke',
  'password_change',
]);
export type RefreshSessionRevokeReason = z.infer<typeof refreshSessionRevokeReasonSchema>;

/**
 * Platform the session was created from. Set at session creation from
 * the `x-hero-platform` header (or heuristics on user-agent when the
 * header is missing).
 *
 * Alias of `heroPlatformSchema` in `common/app-registry.schema.ts`, which
 * is where the vocabulary lives now that `doc.platform` and
 * `User.meta.presence` read it too. The subscription-context name is kept
 * because ~40 call sites read naturally as "the session's platform".
 */
export const refreshSessionPlatformSchema = heroPlatformSchema;
export type RefreshSessionPlatform = HeroPlatform;

/**
 * Which identity collection backs this session. Network-party sessions
 * resolve `userId` against `User`; admin sessions resolve against
 * `AdminUser`.
 */
export const refreshSessionOwnerSchema = z.enum(['user', 'admin']);
export type RefreshSessionOwner = z.infer<typeof refreshSessionOwnerSchema>;

/**
 * Device metadata captured at session creation for audit and
 * device-management UI (`GET /auth/sessions`).
 */
export const refreshSessionDeviceSchema = z.object({
  userAgent: z.string().optional(),
  ip: z.string().optional(),
  platform: refreshSessionPlatformSchema,
  /** device push notification token, when provided at sign-in */
  deviceToken: z.string().optional(),
});
export type RefreshSessionDevice = z.infer<typeof refreshSessionDeviceSchema>;

/**
 * `RefreshSession` — one row per user login on a device. Backs the
 * rotating refresh-token flow described in
 * `apps/backend/src/app/auth/README.md`.
 *
 * The session ID is the JWT `sessionId` claim; the current refresh
 * token's `jti` is `RefreshSession.jti`; historical `jti`s live in
 * `chain[]` (oldest → newest, excluding the current head).
 *
 * Reuse detection: if a refresh token is presented whose `jti`
 * appears in `chain[]` but does NOT equal the current `jti`, the
 * session is revoked wholesale with reason `reuse_detected`.
 *
 * Indexes: `{ userId, revokedAt }`, `{ jti }`, TTL on `expiresAt`.
 */
export const RefreshSessionSchema = createModel({
  userId: objectIdSchema,
  /** Network party vs Hero staff — controls refresh identity lookup. */
  sessionOwner: refreshSessionOwnerSchema.default('user'),
  /** current refresh-token jti (head of the rotation chain) */
  jti: z.string(),
  /** every previously-rotated jti for this session, oldest → newest */
  chain: z.array(z.string()).default([]),
  device: refreshSessionDeviceSchema,
  /**
   * Active org for this session. Updated whenever
   * `/auth/switch-org` rotates the refresh token in-place. Absent
   * for individual users (no org context) and for super-admin
   * sessions.
   */
  activeOrgId: objectIdSchema.optional(),
  lastUsedAt: dateSchema,
  /** absolute session expiry — refresh past this date is rejected */
  expiresAt: dateSchema,
  /** when the session was revoked, if applicable */
  revokedAt: dateSchema.optional(),
  revokeReason: refreshSessionRevokeReasonSchema.optional(),
});

/** `RefreshSession` db model */
export type RefreshSession = z.infer<typeof RefreshSessionSchema.model>;
export type RefreshSessionData = z.infer<typeof RefreshSessionSchema.data>;
export type RefreshSessionPartial = z.infer<typeof RefreshSessionSchema.partial>;
export type RefreshSessionQueryMany = QueryMany<RefreshSession>;
export type RefreshSessionQueryOne = QueryOne<RefreshSession>;
